import React, { useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { api } from './api.js';
import styles from './Reports.module.css';
import { hasPermission, PERMISSIONS, getUserData } from './permissions';

const Reports = () => {
  const [sectores, setSectores] = useState([]);
  const [sectorSeleccionado, setSectorSeleccionado] = useState('Todos');
  const [afiliados, setAfiliados] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [totals, setTotals] = useState({
    enero: 0, febrero: 0, marzo: 0, abril: 0, mayo: 0, junio: 0,
    julio: 0, agosto: 0, septiembre: 0, octubre: 0, noviembre: 0, diciembre: 0, anual: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userData = getUserData();
        const [sectoresData, afiliadosData, pagosData] = await Promise.all([
          api.getSectors(),
          api.getAfiliados(),
          api.getPayments()
        ]);

        setSectores(Array.isArray(sectoresData) ? sectoresData : []);
        setAfiliados(Array.isArray(afiliadosData) ? afiliadosData : []);
        // Ahora pagosData viene como array directo, no como objeto con propiedad payments
        setPagos(Array.isArray(pagosData) ? pagosData : []);
        
        // Si es encargado, pre-seleccionar su sector
        if (userData?.rol === 'encargado_de_sector' && userData?.sector_id) {
          setSectorSeleccionado(userData.sector_id);
        }

        setLoading(false);
      } catch (error) {
        console.error('Error al obtener los datos:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSectorChange = (event) => {
    setSectorSeleccionado(event.target.value);
  };

  const filteredAfiliados = afiliados.filter(afiliado => 
    (afiliado.nombresTitular?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    afiliado.apellidosTitular?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    afiliado.codigoAfiliado?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    afiliado.sector?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Para la tabla, mostrar afiliados del sector seleccionado Y que coincidan con la búsqueda
  const tableAfiliados = (sectorSeleccionado === 'Todos' 
    ? filteredAfiliados 
    : filteredAfiliados.filter(a => a.sector === sectorSeleccionado));

  const calculateTotals = (afiliado) => {
    const totals = {
      enero: 0, febrero: 0, marzo: 0, abril: 0, mayo: 0, junio: 0,
      julio: 0, agosto: 0, septiembre: 0, octubre: 0, noviembre: 0, diciembre: 0, anual: 0
    };

    // Buscar pagos reales para este afiliado
    const pagosAfiliado = pagos.filter(pago => {
      // Comparar con idAfiliado
      return pago.idAfiliado == afiliado.idAfiliado;
    });

    // Procesar cada pago
    pagosAfiliado.forEach((pago) => {
      const fechaPago = new Date(pago.fecha);
      const mes = fechaPago.getMonth();
      const monto = parseFloat(pago.monto) || 0;

      if (!isNaN(monto) && mes >= 0 && mes <= 11) {
        const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
                       'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
        totals[meses[mes]] += monto;
        totals.anual += monto;
      }
    });

    return totals;
  };

  useEffect(() => {
    const newTotals = {
      enero: 0, febrero: 0, marzo: 0, abril: 0, mayo: 0, junio: 0,
      julio: 0, agosto: 0, septiembre: 0, octubre: 0, noviembre: 0, diciembre: 0, anual: 0
    };
  
    tableAfiliados.forEach(afiliado => {
      const afiliadoTotals = calculateTotals(afiliado);
      Object.keys(newTotals).forEach(mes => {
        newTotals[mes] += afiliadoTotals[mes];
      });
    });
  
    setTotals(newTotals);
  
  }, [tableAfiliados, pagos]);

  const generatePDF = () => {
    const doc = new jsPDF();
    const columns = ['Código de Afiliado', 'Nombre y Apellido', 'E', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D', 'Total Anual'];
    const rows = tableAfiliados.map((afiliado) => {
      const totals = calculateTotals(afiliado);
      return [
        afiliado.codigoAfiliado,
        `${afiliado.nombresTitular} ${afiliado.apellidosTitular}`,
        totals.enero, totals.febrero, totals.marzo, totals.abril, totals.mayo,
        totals.junio, totals.julio, totals.agosto, totals.septiembre,
        totals.octubre, totals.noviembre, totals.diciembre, totals.anual
      ];
    });

    rows.push([
      'Totales',
      '',
      totals.enero, totals.febrero, totals.marzo, totals.abril, totals.mayo,
      totals.junio, totals.julio, totals.agosto, totals.septiembre,
      totals.octubre, totals.noviembre, totals.diciembre, totals.anual
    ]);

    doc.autoTable(columns, rows);
    return doc;
  };

  const handleDownloadPDF = () => {
    const doc = generatePDF();
    // En dispositivos móviles, usar un nombre de archivo más corto
    doc.save('reporte.pdf');
  };

  const handleSharePDF = async () => {
    try {
      const doc = generatePDF();
      const pdfBlob = doc.output('blob');
      
      // Crear un objeto File desde el Blob
      const pdfFile = new File([pdfBlob], 'reporte.pdf', { type: 'application/pdf' });

      // Verificar si el navegador soporta la API de compartir
      if (navigator.share) {
        await navigator.share({
          files: [pdfFile],
          title: 'Reporte de Cobros',
          text: 'Compartir reporte de cobros'
        });
      } else {
        // Fallback para WhatsApp si la API de compartir no está disponible
        const pdfUrl = URL.createObjectURL(pdfBlob);
        const whatsappUrl = `whatsapp://send?text=Reporte de Cobros ${encodeURIComponent(pdfUrl)}`;
        window.location.href = whatsappUrl;
      }
    } catch (error) {
      console.error('Error al compartir:', error);
      alert('No se pudo compartir el archivo. Intente descargarlo y compartirlo manualmente.');
    }
  };

  const handlePrint = () => {
    const printArea = document.createElement('div');
    printArea.innerHTML = `
      <div style="padding: 20px;">
        <h1 style="text-align: center;">Reporte de Cobros - ${sectorSeleccionado}</h1>
        <p style="text-align: center;">Fecha: ${new Date().toLocaleDateString()}</p>
        <table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
          <thead>
            <tr>
              <th style="border: 1px solid black; padding: 8px;">Código</th>
              <th style="border: 1px solid black; padding: 8px;">Nombre</th>
              <th style="border: 1px solid black; padding: 8px;">Monto</th>
            </tr>
          </thead>
          <tbody>
            ${filteredAfiliados.map(afiliado => `
              <tr>
                <td style="border: 1px solid black; padding: 8px;">${afiliado.codigoAfiliado}</td>
                <td style="border: 1px solid black; padding: 8px;">${afiliado.nombresTitular} ${afiliado.apellidosTitular}</td>
                <td style="border: 1px solid black; padding: 8px;">${afiliado.totalPagarMensual || ''}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printArea.innerHTML);
    printWindow.document.close();
    printWindow.print();
  };

  const handleViewTemplate = () => {
    const templateWindow = window.open('', '_blank');
    const tableHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Plantilla de Reporte - Cobros</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
              background-color: #f5f5f5;
            }
            .container {
              max-width: 1200px;
              margin: 0 auto;
              background-color: white;
              padding: 30px;
              border-radius: 8px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            h1 {
              text-align: center;
              color: #333;
              margin-bottom: 10px;
            }
            .date-info {
              text-align: center;
              color: #666;
              margin-bottom: 30px;
              font-size: 14px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 30px;
              font-size: 11px;
            }
            th {
              background-color: white;
              color: black;
              padding: 8px;
              text-align: left;
              font-weight: bold;
              border: 1px solid #ddd;
            }
            td {
              padding: 8px;
              border: 1px solid #ddd;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .totals-row {
              background-color: #e8f5e9;
              font-weight: bold;
            }
            .buttons-group {
              display: flex;
              gap: 10px;
              justify-content: center;
              margin: 20px 0;
              flex-wrap: wrap;
            }
            .print-button, .download-button {
              padding: 10px 30px;
              background-color: #2196F3;
              color: white;
              border: none;
              border-radius: 4px;
              cursor: pointer;
              font-size: 16px;
            }
            .download-button {
              background-color: #4CAF50;
            }
            .print-button:hover {
              background-color: #0b7dda;
            }
            .download-button:hover {
              background-color: #45a049;
            }
          </style>
        </head>
        <body>
          <div class="container">
            <h1>Reporte de Cobros - ${sectorSeleccionado}</h1>
            <p class="date-info">Fecha: ${new Date().toLocaleDateString()}</p>
            <table id="reporte-table">
              <thead>
                <tr>
                  <th>Código de Afiliado</th>
                  <th>Nombre y Apellido</th>
                  <th>E</th>
                  <th>F</th>
                  <th>M</th>
                  <th>A</th>
                  <th>M</th>
                  <th>J</th>
                  <th>J</th>
                  <th>A</th>
                  <th>S</th>
                  <th>O</th>
                  <th>N</th>
                  <th>D</th>
                  <th>Total Anual</th>
                </tr>
              </thead>
              <tbody>
                ${tableAfiliados.map((afiliado) => {
                  const afiliadoTotals = calculateTotals(afiliado);
                  return `
                    <tr>
                      <td>${afiliado.codigoAfiliado}</td>
                      <td>${afiliado.nombresTitular} ${afiliado.apellidosTitular}</td>
                      <td>${afiliadoTotals.enero || 0}</td>
                      <td>${afiliadoTotals.febrero || 0}</td>
                      <td>${afiliadoTotals.marzo || 0}</td>
                      <td>${afiliadoTotals.abril || 0}</td>
                      <td>${afiliadoTotals.mayo || 0}</td>
                      <td>${afiliadoTotals.junio || 0}</td>
                      <td>${afiliadoTotals.julio || 0}</td>
                      <td>${afiliadoTotals.agosto || 0}</td>
                      <td>${afiliadoTotals.septiembre || 0}</td>
                      <td>${afiliadoTotals.octubre || 0}</td>
                      <td>${afiliadoTotals.noviembre || 0}</td>
                      <td>${afiliadoTotals.diciembre || 0}</td>
                      <td>${afiliadoTotals.anual || 0}</td>
                    </tr>
                  `;
                }).join('')}
                <tr class="totals-row">
                  <td colspan="2">Totales</td>
                  <td>${totals.enero || ''}</td>
                  <td>${totals.febrero || ''}</td>
                  <td>${totals.marzo || ''}</td>
                  <td>${totals.abril || ''}</td>
                  <td>${totals.mayo || ''}</td>
                  <td>${totals.junio || ''}</td>
                  <td>${totals.julio || ''}</td>
                  <td>${totals.agosto || ''}</td>
                  <td>${totals.septiembre || ''}</td>
                  <td>${totals.octubre || ''}</td>
                  <td>${totals.noviembre || ''}</td>
                  <td>${totals.diciembre || ''}</td>
                  <td>${totals.anual || ''}</td>
                </tr>
              </tbody>
            </table>
            <div class="buttons-group">
              <button class="print-button" onclick="window.print()">Imprimir Plantilla</button>
              <button class="download-button" onclick="descargarPlantillaPDF()">Descargar Plantilla</button>
            </div>
          </div>
          <script src="https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js"></script>
          <script src="https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js"></script>
          <script src="https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.5.31/jspdf.plugin.autotable.min.js"></script>
          <script>
            function descargarPlantillaPDF() {
              const { jsPDF } = window.jspdf;
              const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
              
              const titulo = 'Reporte de Cobros - ${sectorSeleccionado}';
              const fecha = 'Fecha: ' + new Date().toLocaleDateString();
              
              doc.setFontSize(16);
              doc.text(titulo, doc.internal.pageSize.getWidth() / 2, 15, { align: 'center' });
              
              doc.setFontSize(10);
              doc.text(fecha, doc.internal.pageSize.getWidth() / 2, 22, { align: 'center' });
              
              const table = document.getElementById('reporte-table');
              const rows = [];
              
              table.querySelectorAll('tbody tr').forEach(row => {
                const cols = [];
                row.querySelectorAll('td').forEach(col => {
                  cols.push(col.innerText);
                });
                rows.push(cols);
              });
              
              const headers = [];
              table.querySelectorAll('thead th').forEach(header => {
                headers.push(header.innerText);
              });
              
              doc.autoTable({
                head: [headers],
                body: rows,
                startY: 30,
                margin: { top: 30, right: 10, bottom: 10, left: 10 },
                didDrawPage: function(data) {
                  if (data.pageNumber > 1) {
                    doc.setPage(data.pageNumber);
                    doc.setFontSize(10);
                    doc.text(titulo, doc.internal.pageSize.getWidth() / 2, 12, { align: 'center' });
                  }
                }
              });
              
              doc.save('reporte-cobros.pdf');
            }
          </script>
        </body>
      </html>
    `;
    templateWindow.document.write(tableHTML);
    templateWindow.document.close();
  };

  return (
    <div className={styles['reports-container']}>
      <div className={styles.headerSection}>
        <h2>Reportes de Cobros</h2>
        <div className={styles.headerControls}>
          <div className={styles.searchContainer}>
            <input
              type="text"
              placeholder="Buscar por nombre, código..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>
          <div className={styles['sector-select']}>
            <label htmlFor="sector" style={{ margin: 0, whiteSpace: 'nowrap' }}>Sector:</label>
            <select id="sector" value={sectorSeleccionado} onChange={handleSectorChange} style={{ flex: 1, minWidth: '150px' }}>
              <option value="Todos">Todos</option>
              {sectores.map((sector) => (
                <option key={sector.id} value={sector.nombre_sector}>{sector.nombre_sector}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', fontSize: '16px', color: '#666' }}>Cargando datos...</div>
      ) : (
        <div className={styles['table-wrapper']}>
          <div className={styles['table-container']}>
            <table>
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Nombre</th>
                  <th>E</th>
                  <th>F</th>
                  <th>M</th>
                  <th>A</th>
                  <th>M</th>
                  <th>J</th>
                  <th>J</th>
                  <th>A</th>
                  <th>S</th>
                  <th>O</th>
                  <th>N</th>
                  <th>D</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {tableAfiliados.map((afiliado) => {
                  const totals = calculateTotals(afiliado);
                  return (
                    <tr key={afiliado.idAfiliado}>
                      <td>{afiliado.codigoAfiliado}</td>
                      <td style={{ textAlign: 'left' }}>{`${afiliado.nombresTitular} ${afiliado.apellidosTitular}`}</td>
                      <td>{totals.enero || 0}</td>
                      <td>{totals.febrero || 0}</td>
                      <td>{totals.marzo || 0}</td>
                      <td>{totals.abril || 0}</td>
                      <td>{totals.mayo || 0}</td>
                      <td>{totals.junio || 0}</td>
                      <td>{totals.julio || 0}</td>
                      <td>{totals.agosto || 0}</td>
                      <td>{totals.septiembre || 0}</td>
                      <td>{totals.octubre || 0}</td>
                      <td>{totals.noviembre || 0}</td>
                      <td>{totals.diciembre || 0}</td>
                      <td style={{ fontWeight: 600 }}>{totals.anual || 0}</td>
                    </tr>
                  );
                })}
                <tr style={{ backgroundColor: '#e8f5e9', fontWeight: 'bold' }}>
                  <td colSpan="2">Totales</td>
                  <td>{totals.enero || 0}</td>
                  <td>{totals.febrero || 0}</td>
                  <td>{totals.marzo || 0}</td>
                  <td>{totals.abril || 0}</td>
                  <td>{totals.mayo || 0}</td>
                  <td>{totals.junio || 0}</td>
                  <td>{totals.julio || 0}</td>
                  <td>{totals.agosto || 0}</td>
                  <td>{totals.septiembre || 0}</td>
                  <td>{totals.octubre || 0}</td>
                  <td>{totals.noviembre || 0}</td>
                  <td>{totals.diciembre || 0}</td>
                  <td style={{ fontWeight: 'bold' }}>{totals.anual || 0}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
      <div className={styles['actions-container']}>
        <h3>Total Anual: RD$ {totals.anual.toLocaleString()}</h3>
        <div className={styles['buttons-container']}>
          <button onClick={handleDownloadPDF} className={styles['download-button']}>
            Descargar
          </button>
          <button onClick={handlePrint} className={styles['print-button']}>
            Imprimir
          </button>
          <button onClick={handleSharePDF} className={styles['share-button']}>
            Compartir
          </button>
          <button onClick={handleViewTemplate} className={styles['template-button']}>
            Ver Plantilla
          </button>
        </div>
      </div>
    </div>
  );
};

export default Reports;